/** @jsx h */
import { Fragment, h } from 'preact'
import Icon from './icons/icons'
import Link from './link'
import { useState } from 'preact/compat'
import { useLabel } from '../use-label'
import { useGlobal } from '../use-global'
import { EXTERNAL_POINT_OF_SALE, isRetailer } from '../utils'
import clsx from 'clsx'
import dayjs from 'dayjs'

const Confirmation = () => {
  const t = useLabel()
  const [
    {
      purposeMessage,
      selectedPurpose,
      selectedBoutique,
      selectedCountry,
      appointmentDetails,
      personalInfo,
      canvasConfig
    }
  ] = useGlobal()

  const [toggled] = useState(true)
  const comma = selectedBoutique.postalCode === null ? '' : ', '
  const isExternalPointOfSale = selectedBoutique.boutiqueType === EXTERNAL_POINT_OF_SALE

  return (
    <div className='rcc_c_page-confirmation rcc-font-widget rcc-mt-[67px]'>
      <div className='rcc_c_page-confirmation-container rcc-max-w-[560px] rcc-mx-auto'>
        <h2
          className={clsx('rcc-text-3xl rcc-font-medium rcc-text-center rcc-uppercase', {
            'rcc-tracking-widest': canvasConfig.locale !== 'ar'
          })}
        >
          {t('Submit.Submit_Appointment_Confirmation')}
        </h2>

        <p className='rcc_c_page-confirmation-text rcc-text-center rcc-mt-10 rcc-mb-5'>
          {isExternalPointOfSale ? t('Submit.Submit_Thank_You_Note_External') : t('Submit.Submit_Thank_You_Note')}
        </p>

        <div className='rcc_c_card-appointment rcc-bg-primary rcc-text-center rcc-p-5'>
          {/* Boutique */}
          <div className='rcc_c_card_appointment_step rcc-pb-5'>
            <label className='rcc-text-sm rcc-text-gray'>{t('Select_a_Boutique.SB_Filters_Boutique')}</label>
            <p className='rcc-mt-1.5'>{selectedBoutique.commercialName || selectedBoutique.name}</p>
            {selectedCountry === 'JP' ? (
              <Fragment>
                <p className='rcc-mt-1.5'>{selectedBoutique.postalCode}</p>
                <p className='rcc-mt-1.5'>
                  {selectedBoutique.shippingState} {selectedBoutique.city} {selectedBoutique.street}
                </p>
              </Fragment>
            ) : (
              <Fragment>
                <p className='rcc-mt-1.5'>{selectedBoutique.street}</p>
                <p className='rcc-mt-1.5'>
                  {selectedBoutique.city}
                  {comma}
                  {selectedBoutique.postalCode}
                </p>
              </Fragment>
            )}
            {isRetailer(selectedBoutique) && (
              <span className='rcc-inline-block rcc-bg-gray-very-light rcc-text-gray rcc-px-2 rcc-font-semibold rcc-mt-1.5'>
                {t('Select_a_Boutique.SB_Retailer')}
              </span>
            )}
          </div>

          <hr className='rcc-m-0 rcc-w-[240px] rcc-h-px rcc-mx-auto rcc-bg-gray-very-light' />

          {/* Date & Time */}
          <div className='rcc_c_card_appointment_step rcc-py-5'>
            <label className='rcc-text-sm rcc-text-gray'>{t('Appointment_Details.AD_Date')}</label>
            <p className='rcc-mt-1.5'>
              {canvasConfig.locale === 'ar'
                ? dayjs(appointmentDetails.AD_Date, 'YYYY-MM-DD').locale('en-gb').format('MMMM DD YYYY')
                : dayjs(appointmentDetails.AD_Date, 'YYYY-MM-DD').format(dayjs.localeData().longDateFormat('L'))}
            </p>
            {appointmentDetails.AD_Available_Time && (
              <p className='rcc-mt-1.5'>
                {canvasConfig.locale === 'ar'
                  ? dayjs(appointmentDetails.AD_Available_Time, 'HH:mm').format('hh:mm A')
                  : dayjs(appointmentDetails.AD_Available_Time, 'HH:mm').format(
                      dayjs.localeData().longDateFormat('LT')
                    )}
              </p>
            )}
            {isExternalPointOfSale && appointmentDetails.AD_Preferred_Time && (
              <p className='rcc-mt-1.5'>{t(`Appointment_Details.AD_${appointmentDetails.AD_Preferred_Time}`)}</p>
            )}
            <p className='rcc-mt-1.5'>
              {appointmentDetails.AD_Appointment_Type === 'VIRTUAL'
                ? `${t('Select_a_Boutique.SB_Appointment_Type_V')} - ${appointmentDetails.AD_Communication_Channel}`
                : t('Select_a_Boutique.SB_Appointment_Type_IS')}
            </p>
          </div>

          <hr className='rcc-m-0 rcc-w-[240px] rcc-h-px rcc-mx-auto rcc-bg-gray-very-light' />

          {/* Purpose */}
          <div className='rcc_c_card_appointment_step rcc-pt-5'>
            <label className='rcc-text-sm rcc-text-gray'>{t('Purpose_of_Your_Visit.PYV_Purpose_Of_Your_Visit')}</label>
            {purposeMessage && <p className='rcc-mt-1.5'>{purposeMessage}</p>}
            {!purposeMessage && (
              <p className='rcc-mt-1.5'>
                {selectedPurpose.title} - {selectedPurpose.label}{' '}
              </p>
            )}
            {appointmentDetails.AD_Comments && (
              <p className='rcc-mt-1.5 rcc-italic'>{`"${appointmentDetails.AD_Comments}"`}</p>
            )}
          </div>
        </div>
      </div>

      <div className='rcc_c_page-confirmation-save-container rcc-max-w-[560px] rcc-mx-auto'>
        <p className='rcc-text-center rcc-mt-8' data-cs-mask>
          {isExternalPointOfSale
            ? t('Submit.Submit_Confirmation_Sent_External')
            : canvasConfig.direction === 'rtl'
              ? `${personalInfo.PI_Email_Address} :${t('Submit.Submit_Confirmation_Sent')}`
              : `${t('Submit.Submit_Confirmation_Sent')}: ${personalInfo.PI_Email_Address}`}
        </p>
        <div
          className={clsx('rcc-flex rcc-mt-10 rcc-gap-2', {
            'rcc_c_is-hidden': !toggled
          })}
        >
          {canvasConfig.homeLink && (
            <Link
              className='rcc_c_page-confirmation-link rcc-text-center rcc-border rcc-no-underline rcc-px-12 rcc-py-4 rcc-flex-1 rcc-text-text visited:rcc-text-text'
              href={canvasConfig.homeLink}
            >
              {t('Global.Back_to_home')}
            </Link>
          )}

          {canvasConfig.showPrintIcon && (
            <button
              className='rcc_c_page-confirmation-print-icon rcc-border rcc-border-solid rcc-border-text rcc-bg-white rcc-cursor-pointer rcc-h-[50px] rcc-w-[50px] rcc-flex rcc-justify-center rcc-items-center'
              onClick={() => window.print()}
            >
              <Icon name='print' />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Confirmation
